import { NumberParam, StringParam } from 'use-query-params';

import type { GetVulnerabilitiesParams } from '@apis/mr-infra-risk/queries/use-vulnerabilities';
import type { GridSortModel } from '@packages/fenrir/components';

import type {
  RangeFilter,
  VulnerabilityParam,
} from '../../filter-management/filter-map-item';
import type { acceptedParamTypes } from '../../hooks/use-risks-management-filters/filter-types';
import {
  SeverityParam,
  SourceParam,
} from '../../hooks/use-risks-management-filters/filter-types';
import { UnixDateParam } from '../../query-params/unix-date-param';

export const VULNERABILITY_COLUMN_DEFS = {
  ISSUE_ID: 'issueID',
  NAME: 'name',
  RISKS_OPEN: 'riskCountUnresolved',
  RISKS_RESOLVED: 'riskCountResolved',
  CISA_KEV: 'cisaKnownExploit',
  SCORE: 'score',
  SEVERITY: 'severity',
  SOURCE: 'source',
  CVSSV3: 'cvssV3Score',
  FIRST_DETECTED_TIME: 'firstDetectedTime',
  LAST_DETECTED_TIME: 'lastDetectedTime',
  ISSUE_PUBLISH_TIME: 'issuePublishTime',
  ISSUE_UPDATE_TIME: 'issueUpdateTime',
  CVE: 'cve',
  ACTIONS: 'actions',
  SEARCH: 'search', // corner case for search filter
} as const;

export const DEFAULT_SORT: GridSortModel = [
  { field: VULNERABILITY_COLUMN_DEFS.SCORE, sort: 'desc' },
  { field: VULNERABILITY_COLUMN_DEFS.RISKS_OPEN, sort: 'desc' },
];

export const DEFAULT_VULNERABILITY_COLUMN_MODEL = {
  [VULNERABILITY_COLUMN_DEFS.ISSUE_ID]: false,
  [VULNERABILITY_COLUMN_DEFS.NAME]: true,
  [VULNERABILITY_COLUMN_DEFS.RISKS_OPEN]: true,
  [VULNERABILITY_COLUMN_DEFS.RISKS_RESOLVED]: true,
  [VULNERABILITY_COLUMN_DEFS.CISA_KEV]: true,
  [VULNERABILITY_COLUMN_DEFS.SCORE]: true,
  [VULNERABILITY_COLUMN_DEFS.SEVERITY]: true,
  [VULNERABILITY_COLUMN_DEFS.SOURCE]: true,
  [VULNERABILITY_COLUMN_DEFS.CVSSV3]: false,
  [VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME]: false,
  [VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME]: false,
  [VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME]: false,
  [VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME]: false,
  [VULNERABILITY_COLUMN_DEFS.CVE]: true,
  [VULNERABILITY_COLUMN_DEFS.ACTIONS]: true,
};

export const VULNERABILITY_SUPPORTED_SORT_FIELDS: string[] = [
  VULNERABILITY_COLUMN_DEFS.SCORE,
  VULNERABILITY_COLUMN_DEFS.CVSSV3,
  VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME,
  VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME,
  VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME,
  VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME,
  VULNERABILITY_COLUMN_DEFS.CISA_KEV,
  VULNERABILITY_COLUMN_DEFS.RISKS_OPEN,
  VULNERABILITY_COLUMN_DEFS.RISKS_RESOLVED,
  // VULNERABILITY_COLUMN_DEFS.SEVERITY,
];

export const EXPORT_VULNERABILITY_API_NAMES = {
  [VULNERABILITY_COLUMN_DEFS.CVSSV3]: 'cve.primary.cvss.v3.score',
  [VULNERABILITY_COLUMN_DEFS.RISKS_OPEN]: 'riskCountUnresolved',
  [VULNERABILITY_COLUMN_DEFS.RISKS_RESOLVED]: 'riskCountResolved',
};

export type vulnQueryParamType = {
  [key in keyof GetVulnerabilitiesParams]: acceptedParamTypes;
};

export const vulnRangeFilters: Record<
  string,
  RangeFilter<VulnerabilityParam>
> = {
  [VULNERABILITY_COLUMN_DEFS.SCORE]: {
    to: 'toScore',
    from: 'fromScore',
  },
  [VULNERABILITY_COLUMN_DEFS.CVSSV3]: {
    to: 'toCvssV3Score',
    from: 'fromCvssV3Score',
  },
  [VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME]: {
    to: 'toFirstDetectedTime',
    from: 'fromFirstDetectedTime',
  },
  [VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME]: {
    to: 'toLastDetectedTime',
    from: 'fromLastDetectedTime',
  },
  [VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME]: {
    to: 'toIssuePublishTime',
    from: 'fromIssuePublishTime',
  },
  [VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME]: {
    to: 'toIssueUpdateTime',
    from: 'fromIssueUpdateTime',
  },
};

export const vulnQueryParamsMap: vulnQueryParamType = {
  [VULNERABILITY_COLUMN_DEFS.SEVERITY]: SeverityParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.SCORE].to]: NumberParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.SCORE].from]: NumberParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.CVSSV3].to]: NumberParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.CVSSV3].from]: NumberParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME].to]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME].from]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME].to]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME].from]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME].to]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME].from]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME].to]:
    UnixDateParam,
  [vulnRangeFilters[VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME].from]:
    UnixDateParam,
  [VULNERABILITY_COLUMN_DEFS.SEARCH]: StringParam,
  [VULNERABILITY_COLUMN_DEFS.CISA_KEV]: StringParam,
  [VULNERABILITY_COLUMN_DEFS.ISSUE_ID]: StringParam,
  [VULNERABILITY_COLUMN_DEFS.SOURCE]: SourceParam,
};

export const DEFAULT_FILTER = [];
